import { reflect } from '@effector/reflect';
import { TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PROMOCODE_MAX_LENGTH } from 'entities/promocode';
import { Player } from 'shared/api';
import { $t, breakpoints, ModalFormModel, translated } from 'shared/lib';
import { createModalForm, ModalFormProps } from 'shared/ui';
import ActivateIcon from 'shared/ui/assets/activate-icon.svg?react';
import { ActivatePromocodeModel } from '../../model';
import { createActivatePromocodeButton } from '../activate-promocode-button';
import cls from './activate-promocode-modal.module.scss';

export const createActivatePromocodeModal = (
	model: ActivatePromocodeModel,
	cancelModel: Pick<ModalFormModel<Player>, 'modalButtonClicked'>,
) => {
	const ModalForm = createModalForm(model);

	const PromocodeField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'activate-promocode-input',
			maxLength: PROMOCODE_MAX_LENGTH,
			size: breakpoints.md.map((md) => (md ? '2xl' : 'xl')),
			value: model.form.fields.promocode.$value,
			label: translated('activate-promocode.promocode'),
			classNames: {
				root: cls.activatePromocodeModalPromocodeField,
				input: cls.activatePromocodeModalPromocodeFieldInput,
			},
			errorProps: { 'data-test-id': 'activate-promocode-input-error' },
			error: combine([model.form.fields.promocode.$errors, $t], ([errors, t]) => {
				if (errors.length === 0) return false;

				const error = errors[0];

				if (error === 'PLAYER-BALANCE-IS-NOT-ZERO') {
					return t(`errors:http-messages.${error}`);
				}

				return t(`errors:validation.${error}`, {
					fieldName: t('activate-promocode.promocode'),
					maxLength: PROMOCODE_MAX_LENGTH,
				});
			}),
			onChange: (e) => model.form.fields.promocode.change(e.target.value),
		},
	});

	const ActivatePromocodeModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, md] = useUnit([model.$pending, breakpoints.md]);

		return (
			<ModalForm
				ref={ref}
				title={t('activate-promocode.title')}
				submitButtonProps={{
					children: t('activate-promocode.activate'),
					leftSection: <ActivateIcon />,
					loading: pending,
					variant: 'success',
					size: md ? 'xl' : 'lg',
					fullWidth: true,
				}}
				size={md ? '2xl' : 'xl'}
				{...props}
			>
				<PromocodeField />
			</ModalForm>
		);
	});

	const ActivatePromocodeModalButton = createActivatePromocodeButton(model, cancelModel);

	return Object.assign(ActivatePromocodeModal, {
		Button: ActivatePromocodeModalButton,
	});
};
